import { useQuery } from '@tanstack/react-query';
import { campaignsApi, type Campaign } from '../campaignsApi';
import { type AdChannel, channelLabel } from '../campaignChannels';
import { useSessionStore } from '../../../stores/sessionStore';
import './campaign-channel-settings.css';

interface Props {
  campaign: Campaign;
  channels: AdChannel[];
  onChannelsChange: (channels: AdChannel[]) => void;
  metaAccountId: string;
  googleCustomerId: string;
  onMetaAccountChange: (value: string) => void;
  onGoogleAccountChange: (value: string) => void;
  init?: RequestInit;
}

export function CampaignChannelSettings(props: Props) {
  const tenantId = useSessionStore(state => state.activeTenantId);
  const meta = useQuery({ queryKey: ['campaign-accounts', tenantId, 'meta'],
    queryFn: () => campaignsApi.getMetaAccounts(props.init), staleTime: 60000 });
  const google = useQuery({ queryKey: ['campaign-accounts', tenantId, 'google'],
    queryFn: () => campaignsApi.getGoogleAdsAccounts(props.init), staleTime: 60000 });

  function toggle(channel: AdChannel, enabled: boolean) {
    props.onChannelsChange(enabled ? [...props.channels, channel] : props.channels.filter(item => item !== channel));
  }

  return <section className="campaign-channel-section">
    <h4>Comece pelos canais e pelas contas de anúncios</h4>
    <p>Escolha Meta Ads, Google Ads ou os dois. A publicação é feita na conta definida para cada canal.</p>
    <div className="campaign-channel-grid">
      {(['meta', 'google'] as AdChannel[]).map(channel => {
        const isMeta = channel === 'meta';
        const selected = props.channels.includes(channel);
        const linked = Boolean(isMeta ? props.campaign.meta_campaign_id : props.campaign.google_campaign_id);
        const query = isMeta ? meta : google;
        const accounts = isMeta
          ? (meta.data?.accounts || []).map(account => ({ id: account.adAccountId, name: account.name }))
          : (google.data?.accounts || []).map(account => ({ id: account.customerId, name: account.name }));
        const value = isMeta ? props.metaAccountId : props.googleCustomerId;
        return <div key={channel} className="campaign-channel-option" data-selected={selected}>
          <label className="campaign-channel-toggle">
            <input type="checkbox" checked={selected} disabled={linked}
              onChange={event => toggle(channel, event.target.checked)} />
            <strong>{channelLabel(channel)}</strong>
          </label>
          <p>{isMeta ? 'Anúncios no Facebook e Instagram.' : 'Anúncios de Pesquisa com títulos, descrições e palavras-chave.'}</p>
          {selected && <>
            <label className="campaign-channel-field">Conta {channelLabel(channel)}
              <select value={value} disabled={linked || query.isPending}
                onChange={event => (isMeta ? props.onMetaAccountChange : props.onGoogleAccountChange)(event.target.value)}>
                <option value="">Selecione uma conta</option>
                {value && !accounts.some(account => account.id === value)
                  && <option value={value}>{value} (conta vinculada)</option>}
                {accounts.map(account => <option key={account.id} value={account.id}>{account.name} ({account.id})</option>)}
              </select>
            </label>
            {query.isPending && <small>Carregando contas...</small>}
            {query.isError && <div role="alert">Falha ao consultar contas. <button type="button" onClick={() => query.refetch()}>Tentar novamente</button></div>}
            {!query.isPending && !query.isError && !accounts.length && <small>Nenhuma conta conectada. Confira Integrações Externas.</small>}
            {linked && <small>A conta está vinculada à campanha publicada e não pode ser trocada aqui.</small>}
          </>}
        </div>;
      })}
    </div>
    {props.channels.length === 2 && <p>O orçamento diário informado vale para cada canal. A soma dos dois canais corresponde ao dobro desse valor.</p>}
  </section>;
}
